/**
 * Paper fortune teller (cootie catcher): the game's prize draw. A draw costs a
 * free ticket (one a day, one more for finishing the daily quests) or coins
 * earned in play. It is never sold for real money and coins are never sold
 * either. The odds below are shown to the player exactly as rolled
 * (systems/fortune.ts). Prizes are looks only (data/cosmetics.ts `gacha`).
 */
import type { Rarity } from './cosmetics';

export type FortuneTier = Rarity;

export interface FortuneTierDef {
  tier: FortuneTier;
  /** Chance in percent; the four add up to 100. */
  percent: number;
  /** What the tier gives, for the odds panel. */
  prize: string;
}

export const FORTUNE_TIERS: FortuneTierDef[] = [
  { tier: 'common', percent: 55, prize: 'An everyday ink or drawing stamp (hearts, flowers, cafe colours)' },
  { tier: 'rare', percent: 30, prize: 'A cute stamp, a bright ink or a fun victory stamp' },
  { tier: 'epic', percent: 12, prize: 'A ready-made weapon, a dress, a tiara, jewel inks or a special victory stamp' },
  { tier: 'legendary', percent: 3, prize: 'The unicorn horn, the rainbow or a Paper Queen / King victory stamp' },
];

export const FORTUNE_RULES = {
  /** Coins for a draw. */
  drawCost: 60,
  /** Five draws at once (a little cheaper). */
  fiveCost: 270,
  /** Coin draws per day (tickets do not count): a healthy break for everyone. */
  dailyCoinDraws: 20,
  /** Most tickets the player can hold. */
  ticketCap: 5,
  /** A draw without anything new left in its tier pays these coins instead. */
  refund: { common: 15, rare: 30, epic: 60, legendary: 120 } as Record<FortuneTier, number>,
  /** Guarantees: the Nth draw in a row without a prize of this tier or better gets it. */
  pity: { rare: 10, epic: 30, legendary: 80 },
};

export interface FortuneColor {
  id: 'blue' | 'pink' | 'green' | 'yellow';
  /** Spelled out (in the player's language) to count the opens. */
  name: string;
  fill: string;
}

/** The four outer flaps, in the order they are drawn (top-left, top-right, bottom-left, bottom-right). */
export const FORTUNE_COLORS: FortuneColor[] = [
  { id: 'blue', name: 'BLUE', fill: '#9fcdf2' },
  { id: 'pink', name: 'PINK', fill: '#ffb3c6' },
  { id: 'green', name: 'GREEN', fill: '#b6e39a' },
  { id: 'yellow', name: 'YELLOW', fill: '#fbe27a' },
];

/** Numbers inside the teller: one set shows when it opens up/down, the other when it opens sideways. */
export const FORTUNE_NUMBERS: Record<'v' | 'h', number[]> = { v: [1, 4, 5, 8], h: [2, 3, 6, 7] };

/**
 * Written under every flap: one funny fortune comes with each draw, whatever
 * the prize. Silly on purpose, never mean, fine for every age.
 */
export const FORTUNE_SAYINGS: string[] = [
  'Today your eraser will run away. It will be found in your friend’s pencil case.',
  'The teacher will call your name right after a giant yawn.',
  'Your lunch will be delicious. Your friend will want half.',
  'Something wiggly is waiting in your next drawing.',
  'A cat will look at you as if you owe it money.',
  'You will laugh so hard that milk comes out of your nose. Careful!',
  'Your pencil will break at the most exciting part of your drawing.',
  'Today is a great day to draw a monster with seven noses.',
  'You will find a coin on the floor. It is chocolate. Even better!',
  'Your hair will be perfect today. The wind disagrees.',
  'A pigeon admires you from afar.',
  'Rock, paper or scissors? Your gut says paper. Your gut is hungry.',
  'You will sneeze at exactly the wrong moment.',
  'Your crush will say hi. To the person behind you.',
  'Your next monster will be so scary that you get scared too.',
  'A secret admirer has hidden a snack for you. Check your bag! (Maybe.)',
  'Your shoelace will come undone during the most important walk of the day.',
  'You are 87% more stylish than yesterday.',
  'Today you will win. Maybe a battle, maybe an argument with your little brother.',
  'Beware of the class clown. Also, be the class clown.',
  'You will remember the answer three seconds after the test ends.',
  'A ghost wants to be your friend. Say yes, ghosts are cool.',
  'You will draw a perfect circle by accident and nobody will believe you.',
  'The fridge has something tasty. Go look!',
  'Your best friend thinks your drawings are amazing. They are right.',
  'You will dance like nobody is watching. Somebody is watching.',
  'Your future holds a lot of glitter. Like, a LOT.',
  'Dragons will be jealous of your hairstyle.',
  'Smile! Your notebook is proud of you.',
  'You will become a Paper Legend. First, finish your homework.',
  'A brave monster is about to meet its match: you!',
];
